import { Link, usePage } from '@inertiajs/react';
import logo from '@/assets/logo.png';
import { cn } from "@/lib/utils";
import {
  Home,
  Gift,
  ShoppingBag,
  User,
  LogOut,
  Menu,
  ShieldAlert
} from "lucide-react";
import { useState, createContext, useContext } from "react";
import { Button } from "@/components/Button";
import { GiveModal } from "@/features/give/GiveModal";
import { UserAvatar } from "@/components/UserAvatar";
import { router } from '@inertiajs/react';

export const LayoutContext = createContext();

export function useLayout() {
  return useContext(LayoutContext);
}

const SidebarItem = ({ icon: Icon, label, href, active, onClick }) => {
  const Component = onClick ? 'button' : Link;
  return (
    <Component
      href={!onClick ? href : undefined}
      onClick={onClick}
      className={cn(
        "flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 w-full text-left",
        active
          ? "bg-bambu-green text-white font-semibold"
          : "text-bambu-black hover:bg-bambu-green-pastel"
      )}
    >
      <Icon className="w-5 h-5" />
      <span>{label}</span>
    </Component>
  );
};

// Main App Layout
export default function AppLayout({ children }) {
  const { auth, users = [], values = [] } = usePage().props;
  const { url } = usePage(); // Get current URL

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isGiveModalOpen, setIsGiveModalOpen] = useState(false);

  const currentUser = auth?.user || {};
  const isAdmin = currentUser.role_level >= 2; // Assuming role_level comes from Rails

  const navItems = [
    { icon: Home, label: "Inicio", href: "/" },
    { icon: Gift, label: "Dar puntos", onClick: () => setIsGiveModalOpen(true) },
    { icon: User, label: "Mi perfil", href: "/profile" },
  ];

  if (isAdmin) {
    navItems.push({ icon: ShieldAlert, label: "Admin", href: "/admin" });
  }

  // Check if we are on the Feed page to apply special "dashboard" layout (no global scroll)
  const isFeedPage = url === '/';

  const handleSignOut = () => {
    router.delete('/logout');
  };

  const handleRefresh = () => {
    router.reload();
  };

  return (
    <div className="h-screen bg-bambu-gray-light font-montserrat flex overflow-hidden">
      {/* Mobile Menu Button */}
      <div className="fixed top-0 left-0 w-full bg-white p-4 z-50 md:hidden flex justify-between items-center shadow-md">
        <span className="font-bold text-bambu-green text-lg">bambúcode</span>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          <Menu className="w-6 h-6 text-bambu-black" />
        </button>
      </div>

      {/* Sidebar - Desktop & Mobile */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-bambu-green-pastel transform transition-transform duration-300 ease-in-out md:translate-x-0",
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full",
          "md:relative md:h-screen md:overflow-y-auto"
        )}
      >
        <div className="p-6 flex flex-col h-full min-h-min">
          <div className="mb-8 hidden md:block px-2">
            <img
              src={logo}
              alt="bambúcode"
              className="h-10 w-auto object-contain"
            />
          </div>

          {/* User Brief */}
          <div className="mb-6 p-4 bg-bambu-green-pastel/30 rounded-lg flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-white font-bold overflow-hidden shadow-sm border border-gray-100">
              <UserAvatar user={currentUser} size={40} tooltipSide="right" />
            </div>
            <div>
              <p className="text-sm font-bold text-bambu-black truncate max-w-[120px]">{currentUser.full_name || currentUser.email}</p>
              <p className="text-xs text-gray-500 font-medium">
                {currentUser.department?.name || 'General'}
              </p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-2">
            {navItems.map((item, index) => (
              <SidebarItem
                key={index}
                {...item}
                active={url === item.href}
              />
            ))}
          </nav>

          {/* Logout */}
          <div className="mt-auto pt-4">
            <Button
              variant="ghost"
              className="w-full justify-start text-bambu-red hover:bg-red-50 hover:text-red-600"
              onClick={handleSignOut}
            >
              <LogOut className="w-5 h-5 mr-3" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={cn(
          "flex-1 pt-20 md:pt-0 transition-all duration-200",
          isFeedPage
            ? "h-screen overflow-hidden"
            : "h-screen overflow-y-auto p-4 md:p-8"
        )}
      >
        <LayoutContext.Provider value={{ openGiveModal: () => setIsGiveModalOpen(true) }}>
          {children}
        </LayoutContext.Provider>
      </main>

      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Global Give Modal */}
      <GiveModal
        isOpen={isGiveModalOpen}
        onClose={() => setIsGiveModalOpen(false)}
        onSuccess={handleRefresh}
        users={users}
        companyValues={values}
      />
    </div>
  );
}
